import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle, cn, Mono } from "@athena/ui";
import { alumnusPath, fullName } from "../helpers/client";
import { useOpenedFromHere } from "../helpers/openedFrom";
import { formatMilestoneDay, type Milestone, type MilestoneWhen, type SoberAnniversary } from "../helpers/milestones";
import { MilestoneBadge } from "./MilestoneBadge";

/** Today's is the brand's teal, the loudest thing on the page; a week out sits quieter. */
const TONE = {
    today: "border-secondary/40 bg-secondary/10 [&>svg]:text-text-link",
    inAWeek: "[&>svg]:text-text-muted",
} as const satisfies Record<MilestoneWhen, string>;

interface MilestonesCalloutProps {
    title: string;
    icon: ReactNode;
    when: MilestoneWhen;
    birthdays: Milestone[];
    anniversaries: SoberAnniversary[];
}

type Entry = { kind: "birthday"; milestone: Milestone } | { kind: "soberAnniversary"; milestone: SoberAnniversary };

/**
 * Birthdays and sober anniversaries worth knowing about now — today's, or those a week from today,
 * while there is still time to post a card — at the top of the dashboard whatever month it shows.
 * Each with its badge and a link to the person. Nothing is drawn when there are none.
 */
export function MilestonesCallout({ title, icon, when, birthdays, anniversaries }: MilestonesCalloutProps) {
    const openedFrom = useOpenedFromHere();
    const entries: Entry[] = [
        ...birthdays.map((milestone) => ({ kind: "birthday" as const, milestone })),
        ...anniversaries.map((milestone) => ({ kind: "soberAnniversary" as const, milestone })),
    ].sort((a, b) => a.milestone.date.getTime() - b.milestone.date.getTime());
    if (entries.length === 0) {
        return null;
    }
    return (
        // A status, not an alert: it is news, and an assertive announcement on every visit would be noise.
        <Alert role="status" aria-label={title} className={cn(TONE[when])}>
            {icon}
            <AlertTitle>{title}</AlertTitle>
            <AlertDescription>
                <ul className="mt-2 flex flex-col gap-2">
                    {entries.map((entry) => {
                        const { client, date } = entry.milestone;
                        return (
                            <li key={`${entry.kind}-${client.id}`} className="flex flex-wrap items-center gap-2">
                                {entry.kind === "birthday" ? (
                                    <MilestoneBadge kind="birthday" when={when} />
                                ) : (
                                    <MilestoneBadge kind="soberAnniversary" years={entry.milestone.years} when={when} />
                                )}
                                <Link
                                    to={alumnusPath(client.id)}
                                    state={openedFrom}
                                    className="font-medium underline-offset-4 hover:underline"
                                >
                                    {fullName(client)}
                                </Link>
                                {when === "inAWeek" ? (
                                    <Mono as="span" size="xs" muted>
                                        {formatMilestoneDay(date)}
                                    </Mono>
                                ) : null}
                            </li>
                        );
                    })}
                </ul>
            </AlertDescription>
        </Alert>
    );
}
